import React, { useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { Urls } from "../../Urls";
import styles from "./GlobalGridMap.module.css";
import arrow from "./arrow.png";

const regionCenter = {
  india: {
    label: "India",
    coordinates: [78.748, 23.8438],
  },
  europe: {
    label: "Europe",
    coordinates: [10.0183, 50.7054],
  },
  china: {
    label: "China",
    coordinates: [104.1954, 35.8617],
  },
  australia: {
    label: "Australia",
    coordinates: [134.755, -25.2744],
  },
  united_states: {
    label: "United States",
    coordinates: [-98.5795, 39.8283],
  },
};

mapboxgl.workerClass =
  require("worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker").default; // eslint-disable-line

const GlobalGridMap = ({ networkData, loading }) => {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const history = useHistory();
  const location = useLocation();

  const getRegionFromUrl = () => {
    const params = new URLSearchParams(location.search);
    const regionParam = params.get("region");
    if (regionParam && regionCenter[regionParam]) {
      return regionCenter[regionParam].coordinates;
    }
    return regionCenter.india.coordinates;
  };

  const [selectedRegion, setSelectedRegion] = useState(getRegionFromUrl());
  const [lanesData, setLanesData] = useState([]);

  useEffect(() => {
    const regionCoords = getRegionFromUrl();
    setSelectedRegion(regionCoords);
  }, [location.search]);

  useEffect(() => {
    if (networkData && (networkData.length > 0 || networkData.type === 'FeatureCollection')) {
      setLanesData(networkData);
    }
  }, [networkData]);

  useEffect(() => {
    if (!mapRef.current) {
      mapRef.current = new mapboxgl.Map({
        accessToken: Urls.mapboxAccessToken,
        container: mapContainerRef.current,
        style: "mapbox://styles/mapbox/light-v11",
        center: selectedRegion,
        zoom: 2,
        maxZoom: 6,
        projection: "globe",
      });

      mapRef.current.on("load", () => {
        // Add the single source for both layers
        mapRef.current.addSource("lanes-source", {
          type: "geojson",
          data: {
            type: "FeatureCollection",
            features: [],
          },
        });

        // Add Line Layer
        mapRef.current.addLayer({
          id: "worldMap",
          type: "line",
          source: "lanes-source",
          paint: {
            "line-color": "#045dbf",
            "line-width": 1,
            "line-opacity": 0.3,
          },
        });

        // Load the arrow image
        mapRef.current.loadImage(arrow, (error, image) => {
          if (error) throw error;
          if (!mapRef.current.hasImage("arrow-icon")) {
            mapRef.current.addImage("arrow-icon", image);
          }

          // Add Arrow Symbol Layer after image is loaded
          if (mapRef.current.getSource("lanes-source")) {
            mapRef.current.addLayer({
              id: "arrows-layer",
              type: "symbol",
              source: "lanes-source",
              layout: {
                "symbol-placement": "line",
                "symbol-spacing": 250,
                "icon-image": "arrow-icon",
                "icon-size": 0.5,
                "icon-rotation-alignment": "map",
                "icon-rotate": ["line-progress"],
                "icon-offset": [0, 0],
              },
              paint: {
                "icon-opacity": 0.5,
              },
            });
          }
        });
      });
    }

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (mapRef.current) {
      mapRef.current.flyTo({
        center: selectedRegion,
        essential: true,
        zoom: 2.8,
        duration: 1500,
        easing: (t) => t * (2 - t),
      });
    }
  }, [selectedRegion]);

  useEffect(() => {
    if (lanesData.length === 0 && !lanesData.features) return;
    if (!mapRef.current) return;

    let data;

    // Check if the data is already GeoJSON
    if (lanesData.type === 'FeatureCollection') {
      data = lanesData;
    } else {
      // Fallback for legacy JSON Array support
      data = {
        type: "FeatureCollection",
        features: lanesData.map((lane) => ({
          type: "Feature",
          geometry: {
            type: "LineString",
            coordinates: [
              [lane.origin.lon, lane.origin.lat],
              [lane.destination.lon, lane.destination.lat],
            ],
          },
        })),
      };
    }

    // Only update data if the source exists
    if (mapRef.current.isStyleLoaded() || mapRef.current.loaded()) {
      const source = mapRef.current.getSource("lanes-source");
      if (source) {
        source.setData(data);
      }
    } else {
      mapRef.current.once("load", () => {
        const source = mapRef.current.getSource("lanes-source");
        if (source) {
          source.setData(data);
        }
      });
    }
  }, [lanesData]);

  const handleRegionChange = (e) => {
    const regionKey = e.target.value;
    history.push({
      pathname: location.pathname,
      search: `?region=${regionKey}`,
    });
  };

  return (
    <div>
      {loading && (
        <div className={styles.subtleLoader}>
          Loading routes...
        </div>
      )}
      <div ref={mapContainerRef} className={styles.mapContainer} />
      <div className={styles.legendContainer}>
        <select
          value={Object.keys(regionCenter).find(
            (key) =>
              regionCenter[key].coordinates.toString() ===
              selectedRegion.toString()
          )}
          onChange={handleRegionChange}
          className={styles.countryDropdown}
        >
          {Object.keys(regionCenter).map((key) => (
            <option key={key} value={key}>
              {regionCenter[key].label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

export default GlobalGridMap;
